import { Div, Button } from '@codesuma/baseline'
import dayEvents from '../../../services/day-events'
import adoptionScan from '../../../services/event-adoption-scan'
import { isCandidate } from '../../../services/event-adoption'
import { IPhoneEvent, eventTime, fromPhone } from '../../../services/phone-events'
import { IDayCell } from '../../../services/calendar'
import { PhoneMark } from '../../../components/phone-mark'
import style from './index.module.css'

// The day's events, sitting between its tasks and its occasions: what is left
// to do first, then what the diary has on it, then what the day is named for.
//
// Both sources are in this one list — Emrouz's own events and the phone's — and
// deliberately so: a day does not care which store an appointment came from.
// Where they part company is on the tap. Emrouz's own open its editor; the
// phone's open the phone's own calendar, which is where they are changed, since
// Emrouz only reads them. A phone row carries a small phone to say so.
//
// A row the adoption scan has flagged carries a mark of its own, which is a
// second tap target: the row still opens as above, the mark offers to turn a
// yearly event into an occasion. Emits 'open' and 'adopt' with the row's event.
//
// One block of rows and no heading of its own — it shares a section with the
// day's occasions, and day-slide/happenings.ts names the two of them. A day with
// none draws nothing at all.
export const PhoneEvents = (day: IDayCell & { isToday: boolean }) => {
    const base = Div()

    const events = dayEvents.forDay(day)
    if (!events.length) {
        base.style({ display: 'none' })
        return base
    }

    const candidates = adoptionScan.all()

    for (const event of events) {
        // A div and not a button: a candidate row carries a real button inside
        // it, and a button inside a button is not something a browser will lay
        // out. Same shape the day cells in the month grid take.
        const row = Div()
        row.addClass(style.event)
        row.attr('role', 'button')
        row.attr('tabindex', '0')
        row.attr('aria-label', fromPhone(event)
            ? `${event.title}، در تقویم گوشی باز می‌شود`
            : `ویرایش ${event.title}`)
        row.el.onclick = () => base.emit('open', event)
        row.el.addEventListener('keydown', (e: KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                row.el.click()
            }
        })

        const title = Div(event.title)
        if (fromPhone(event)) title.append(PhoneMark())
        row.append(title)

        // The hour, and the calendar it came from — which is what tells a work
        // event from a family one when both fall on the same day. Emrouz's own
        // events have no calendar behind them to name, and say only the hour.
        const time = eventTime(event)
        const note = Div(event.calendar ? `${time} — ${event.calendar}` : time)
        note.addClass(style.eventNote)
        row.append(note)

        if (isCandidate(candidates, event)) {
            row.addClass(style.eventCandidate)
            const mark = Button('؟')
            mark.addClass(style.adopt)
            mark.attr('aria-label', `${event.title} را در تقویم شمسی یا قمری نگه دارم؟`)
            // A real listener rather than mark.on('click'): baseline's Button
            // already owns el.onclick and emits 'click' with no arguments, so a
            // handler registered through .on() never sees the DOM event and has
            // nothing to stop. Without that stop the tap carries on to the row
            // underneath and opens the editor instead of asking the question.
            mark.el.addEventListener('click', (e: MouseEvent) => {
                e.stopPropagation()
                base.emit('adopt', event)
            })
            row.append(mark)
        }

        base.append(row)
    }

    return base
}

// Where one of Emrouz's own events goes when it is tapped: its editor, by id.
export const eventRoute = (cell: IDayCell, event: IPhoneEvent) =>
    `/event?d=${cell.gy}-${cell.gm}-${cell.gd}&id=${event.eventId}`

// Where the adoption dialog sends an event it is asked to convert: the occasion
// wizard, on the same day, with the name filled in and the phone's own event id
// carried along so the original can be left out of the list afterwards.
export const adoptRoute = (cell: IDayCell, event: IPhoneEvent) =>
    `/event?kind=occasion&d=${cell.gy}-${cell.gm}-${cell.gd}`
    + `&title=${encodeURIComponent(event.title)}`
    + `&source=${encodeURIComponent(event.id)}`
